/* eslint-disable max-lines-per-function */
/* eslint-disable multiline-ternary */
import React from "react";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";
import {
  renderQuestionIdentityOptionWithTypeChip,
  renderQuestionNameOptionWithTypeChip,
} from "./form-builder-question-autocomplete-option";
import { BuilderQuestion } from "./form-builder-utils";
import {
  FormQuestionVariant,
  getArrayChildQuestionOptions,
  QuestionGroupsBySection,
} from "./form-builder-shared";
import {
  getInvalidContextTokens,
  isValidQuestionIdentifier,
  QUESTION_IDENTIFIER_RULE,
  CONTEXT_VALUE_RULE,
} from "./question-identifier-validation";
import FormBuilderQuestionDataEditor from "./form-builder-question-data-editor";

type Props = {
  question: BuilderQuestion;
  questionType: string;
  questionKey: string;
  questionNameOptions: string[];
  existingQuestions: Record<string, string>;
  contextSuggestions: string[];
  variantOptions: FormQuestionVariant[];
  questionIdentityOptions: string[];
  questionIdentityLabelMap: Record<string, string>;
  questionIdentityTypeMap: Record<string, string>;
  questionGroupsBySection: QuestionGroupsBySection;
  canEditIdentity: boolean;
  onUpdateQuestion: (
    questionId: string,
    update: (question: BuilderQuestion) => BuilderQuestion,
  ) => void;
  onApplyVariant: (
    questionId: string,
    variant: FormQuestionVariant | null,
    preserveIdentity?: boolean,
  ) => void;
};

const FormBuilderQuestionCardFields: React.FC<Props> = ({
  question,
  questionType,
  questionKey,
  questionNameOptions,
  existingQuestions,
  contextSuggestions,
  variantOptions,
  questionIdentityOptions,
  questionIdentityLabelMap,
  questionIdentityTypeMap,
  questionGroupsBySection,
  canEditIdentity,
  onUpdateQuestion,
  onApplyVariant,
}) => {
  const questionName = question.name.trim();
  const questionAlias = question.alias.trim();
  const questionNameInvalid = Boolean(questionName) && !isValidQuestionIdentifier(questionName);
  const questionAliasInvalid = Boolean(questionAlias) && !isValidQuestionIdentifier(questionAlias);
  const invalidContextTokens = getInvalidContextTokens(question.contextText);
  const questionContextInvalid = invalidContextTokens.length > 0;
  const hasQuestionName = Boolean(questionName);
  let questionNameHelperText = "";
  if (canEditIdentity) {
    questionNameHelperText = questionNameInvalid ? QUESTION_IDENTIFIER_RULE : "";
  }

  let questionContextHelperText = "";
  if (canEditIdentity) {
    questionContextHelperText = questionContextInvalid
      ? `Invalid context: ${invalidContextTokens.join(", ")}. ${CONTEXT_VALUE_RULE}.`
      : CONTEXT_VALUE_RULE;
  }
  let variantHelperText = "Select Question Name first to see variants.";
  if (hasQuestionName) {
    variantHelperText = variantOptions.length === 0 ? "No saved variants for selected question name." : "";
  }

  const childOptions = questionType === "array"
    ? getArrayChildQuestionOptions(
      questionKey,
      question.childrenKeys,
      questionGroupsBySection.sectionAssignableIdentityOptions,
    )
    : questionIdentityOptions.filter((optionKey) => optionKey !== questionKey);

  const updateQuestionFields = (update: Partial<BuilderQuestion>) => {
    onUpdateQuestion(question.draftId, (current) => ({
      ...current,
      ...update,
    }));
  };

  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      <Grid size={{ xs: 12, md: 5 }}>
        <Autocomplete
          freeSolo
          options={questionNameOptions}
          value={question.name}
          getOptionLabel={(option) => option}
          renderOption={renderQuestionNameOptionWithTypeChip(existingQuestions)}
          onInputChange={(_, newValue) => {
            if (!canEditIdentity) return;
            updateQuestionFields({ name: newValue.trim() });
          }}
          disabled={!canEditIdentity}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Question Name"
              size="small"
              error={canEditIdentity && questionNameInvalid}
              helperText={questionNameHelperText}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Autocomplete
          freeSolo
          options={contextSuggestions}
          value={question.contextText}
          onChange={(_, newValue) => {
            if (!canEditIdentity) return;
            updateQuestionFields({ contextText: `${newValue || ""}`.trim() });
          }}
          onInputChange={(_, newValue) => {
            if (!canEditIdentity) return;
            updateQuestionFields({ contextText: newValue.trim() });
          }}
          disabled={!canEditIdentity}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Context"
              error={canEditIdentity && questionContextInvalid}
              helperText={questionContextHelperText}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Autocomplete
          options={variantOptions}
          disabled={!hasQuestionName}
          getOptionLabel={(option) =>
            `v${option.version} - ${option.title} (${option.usedBy} uses)`
          }
          value={null}
          onChange={(_, newValue) => onApplyVariant(question.draftId, newValue, !canEditIdentity)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Load Existing Variant"
              size="small"
              helperText={variantHelperText}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <TextField
          size="small"
          fullWidth
          label="Title"
          value={question.title}
          onChange={(event) => {
            updateQuestionFields({ title: event.target.value });
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TextField
          size="small"
          fullWidth
          label="Alias"
          value={question.alias}
          onChange={(event) => {
            updateQuestionFields({ alias: event.target.value });
          }}
          error={questionAliasInvalid}
          helperText={questionAliasInvalid ? QUESTION_IDENTIFIER_RULE : ""}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={question.required}
              onChange={(event) => {
                updateQuestionFields({ required: event.target.checked });
              }}
            />
          )}
          label="Required"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          size="small"
          fullWidth
          label="Conditional Values (for cond.includes)"
          helperText="Comma-separated values; empty means no condition."
          value={question.condValuesText}
          onChange={(event) => {
            updateQuestionFields({ condValuesText: event.target.value });
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Autocomplete
          multiple
          options={childOptions}
          value={question.childrenKeys}
          getOptionLabel={(optionKey) => questionIdentityLabelMap[optionKey] || optionKey}
          renderOption={renderQuestionIdentityOptionWithTypeChip(
            questionIdentityLabelMap,
            questionIdentityTypeMap,
          )}
          onChange={(_, newValue) => {
            updateQuestionFields({ childrenKeys: newValue });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={questionType === "array" ? "Array Children Links" : "Children Links"}
              size="small"
              helperText={
                questionType === "array"
                  ? "Saved to question.data.children"
                  : "Saved to question.children"
              }
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormBuilderQuestionDataEditor
          question={question}
          questionType={questionType}
          onUpdateQuestion={onUpdateQuestion}
        />
      </Grid>
    </Grid>
  );
};

export default FormBuilderQuestionCardFields;
